import _ from 'underscore'
import Marionette from 'backbone.marionette'
import vent from 'libraries/vent'
import I18n from 'i18n'
import AgentTopic from 'models/agentTopic'
import Users from 'collections/usersInstance'
import CreateSocialMediaMonitorController from 'controllers/agents/createSocialMediaMonitorController'
import SourceCategorySelectionController from 'controllers/agents/settings/sourceCategorySelectionController'
import AgentTopicController from 'controllers/agents/agentTopicController'
import NewAgentTopicController from 'controllers/agents/newAgentTopicController'
import EditAgentView from 'views/agents/settings/editAgentView'
import EditAgentLandingPageView from 'views/agents/settings/editAgentLandingPageView'
import DeletionModalView from 'views/deletionModalView'
import EditAgentTopicsController from 'controllers/agents/settings/editAgentTopicsController'
import EditAgentSourceSelectionController from 'controllers/agents/settings/editAgentSourceSelectionController'
import EditAgentSocialMediaMonitorController from 'controllers/agents/settings/editAgentSocialMediaMonitorController'
import EditAgentSettingsController from 'controllers/agents/settings/editAgentSettingsController'
import Notificator from 'libraries/notificator'
import sessionInstance from 'models/sessionInstance'
import Limits from 'models/limits'
import { AgentsPermissions } from "components/agents/permissions";
import EditAgentUnsubscribeButtonController from 'controllers/agents/settings/editAgentUnsubscribeButtonController'
import AgentTimeframeController from "controllers/agents/agentTimeframeController";

export default class EditAgentController extends Marionette.Controller {
    initialize(options) {
        _.bindAll(this, '_saveFavoriteSourceGroups', '_saveSourceCategories', '_updateFavoriteSourceGroups');
        this.showRightHandContainer = options.showRightHandContainer;
        this.agent                  = options.agent;
        this.agent.fetch();
        this.agent.fetchTopics();
        this.agent.fetchSocialMediaMonitors();

        this.view  = new EditAgentView({
            agent: this.agent,
            user: Users.get(this.agent.get('user_id'))
        });

        this._registerListeners();
        this._showEditLandingPageView();
        this.sourceGroupQueue = [];
        this.sourceCategoryQueue = [];
        this.isProcessingSourceGroups = false;
        this.isProcessingSourceCategories = false;
        this.pendingSourceGroupIds = null;
        this.pendingSourceCategoryIds = null;
    }

    onClose() {
        this._setAgentCreationFinishedFlag();
    }

    _showEditLandingPageView() {
        this._deselectSelections();
        this.showRightHandContainer(new EditAgentLandingPageView());
        vent.trigger('layout:focus-left');
    }

    _showCreateSocialMediaMonitor() {
        this._deselectSelections();
        const createSMM = new CreateSocialMediaMonitorController(
            {
                agent: this.agent
            }
        );
        this.showRightHandContainer(createSMM.view);
        vent.trigger('layout:focus-right');
        this.listenTo(createSMM, 'social_media_monitor:saved', this._showEditLandingPageView);
        this.listenTo(createSMM, 'social_media_monitor:cancelled', this._showEditLandingPageView);
        this.listenTo(createSMM, 'social_media_monitor:limitReached', this._reactToTopicSocialMediaMonitorLimitReached);
        this.listenTo(createSMM, 'social_media_monitor:globalLimitReached', this._reactToGlobalTopicSocialMediaMonitorLimitReached);
        this.listenTo(createSMM, 'social_media_monitor:userNotFound', this._reactToTopicSocialMediaMonitorUserNotFound);
        this.listenTo(createSMM, 'social_media_monitor:invalidUsername', this._reactToTopicSocialMediaMonitorInvalidUsername);
    }

    _showSourceGroupAndCategories() {
        this._showSourceCategories();
        this._showFavoriteSourceGroups();
    }

    _showFavoriteSourceGroups() {
        this.listenTo(
            this.categoryController.sourceGroupController,
            "changed:favoriteSourceGroups",
            this._updateFavoriteSourceGroups
        );
    }

    _showSourceCategories() {
        const feature =
          sessionInstance.hasFeature('search_agent_topics_with_expert_search');
        let excludedCategoryIdentifiers = [];
        if (feature) {
          excludedCategoryIdentifiers = ['sm', 'rtv']
        }
        this._deselectSelections({ except: ['SourceSelections'] });
        const sourceExclusions = this.agent.get('source_exclusions');
        this.categoryController = new SourceCategorySelectionController({
            sourceCategoryIds: this.agent.get('source_category_ids'),
            sourceExclusions: sourceExclusions,
            excludedCategoryIdentifiers: excludedCategoryIdentifiers,
            agent: this.agent,
            sourceExclusionsPerAgent: this.limits.get('source_exclusions_per_agent'),
        });

        this.listenTo(
            this.categoryController,
            "changed:sourceCategories",
            this._updateSourceCategories
        );
        this.showRightHandContainer(this.categoryController.view);
        vent.trigger('layout:focus-right');
    }

    _showEditTopic(topic) {
        this._deselectSelections();
        const agentTopicController = this._topicController(topic);
        this.listenTo(agentTopicController, 'topic:saved',      this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:cancelled',  this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:deleted',    this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:render', this._reRenderTopicForm);
        this.showRightHandContainer(agentTopicController.view);
        vent.trigger('layout:focus-right');
    }

    _reRenderTopicForm(topic, props) {
        const agentTopicController = this._topicController(topic, props);
        this.listenTo(agentTopicController, 'topic:saved',      this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:cancelled',  this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:deleted',    this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:render', this._reRenderTopicForm);
        this.listenTo(agentTopicController, 'topic:limitReached', this._reactToTopicSocialMediaMonitorLimitReached);
        this.listenTo(agentTopicController, 'topic:globalLimitReached', this._reactToGlobalTopicSocialMediaMonitorLimitReached);
        this.showRightHandContainer(agentTopicController.view);
        vent.trigger('layout:focus-right');
    }

    _reactToTopicSocialMediaMonitorLimitReached() {
        Notificator.showErrorNotification(
            I18n.t('webapp.notifications.error.limit_topics_monitors')
        );
    }

    _reactToGlobalTopicSocialMediaMonitorLimitReached() {
        Notificator.showErrorNotification(
            I18n.t('webapp.notifications.error.global_limit_topics_monitors')
        );
    }

    _reactToTopicSocialMediaMonitorUserNotFound() {
        Notificator.showErrorNotification(
            I18n.t('webapp.notifications.error.user_does_not_exist')
        );
    }

    _reactToTopicSocialMediaMonitorInvalidUsername() {
        Notificator.showErrorNotification(
            I18n.t('webapp.notifications.error.invalid_googleplus_username')
        );
    }

    _topicController(topic, props) {
        const feature =
          sessionInstance.hasFeature('search_agent_topics_with_expert_search');
        if (feature) {
          return new NewAgentTopicController({
            agent: this.agent,
            topic: topic,
            props: props
          });
        } else {
          return new AgentTopicController({ limits: this.limits, agent: this.agent, topic: topic });
        }
    }

    _showNewTopic() {
        this._deselectSelections();
        const agentTopicController = this._topicController(
          new AgentTopic({agent: this.agent})
        );
        this.listenTo(agentTopicController, 'topic:saved',      this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:cancelled',  this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:deleted',    this._closeTopicForm);
        this.listenTo(agentTopicController, 'topic:render', this._reRenderTopicForm);
        this.listenTo(agentTopicController, 'topic:limitReached', this._reactToTopicSocialMediaMonitorLimitReached);
        this.listenTo(agentTopicController, 'topic:globalLimitReached', this._reactToGlobalTopicSocialMediaMonitorLimitReached);
        this.showRightHandContainer(agentTopicController.view);
        vent.trigger('layout:focus-right');
    }

    _updateFavoriteSourceGroups(sourceGroups, isActive) {
        const currentIds = new Set(this.agent.get('source_group_ids'));
        const updatedIds = new Set(_.pluck(sourceGroups, 'id'));

        const finalSourceGroupIds = isActive
        ? [...new Set([...currentIds, ...updatedIds])]
        : [...new Set([...currentIds].filter(id => !updatedIds.has(id)))];

        this.pendingSourceGroupIds = finalSourceGroupIds;
        this.agent.set({ source_group_ids: finalSourceGroupIds }, { silent: true });

        this.sourceGroupQueue = [...this.sourceGroupQueue, { ids: updatedIds, isActive }];
        clearTimeout(this.updateFavoriteSourceGroupsTimer);
        this.updateFavoriteSourceGroupsTimer = setTimeout(() => {
            this._processSourceGroupQueue();
        }, 1500);
    }

    _processSourceGroupQueue() {
        if (this.isProcessingSourceGroups || this.sourceGroupQueue.length === 0) {return;}

        this.isProcessingSourceGroups = true;
        const currentIds = new Set(this.agent.get('source_group_ids'));

        const mergedIds = this.sourceGroupQueue.reduce((acc, { ids, isActive }) => {
            ids.forEach(id => isActive ? acc.add(id) : acc.delete(id));
            return acc;
        }, new Set(currentIds));
        this.sourceGroupQueue = [];

        const finalSourceGroupIds = [...mergedIds];
        this.agent.set({ source_group_ids: finalSourceGroupIds });
        this._saveFavoriteSourceGroups()
        .then(() => {
            if (this.pendingSourceCategoryIds) {
                this.agent.set({ source_category_ids: this.pendingSourceCategoryIds }, { silent: true });
            }
            this.isProcessingSourceGroups = false;
            if (this.sourceGroupQueue.length > 0) {
                this._processSourceGroupQueue();
            }
        })
        .fail(() => {
            this.isProcessingSourceGroups = false;
        });
    }

    _saveFavoriteSourceGroups() {
        return this.agent.save(null, { updateOnly: 'source_groups' })
            .fail(() => {
                Notificator.showErrorNotification(I18n.t('webapp.agents.edit.agent_subscriptions.request_error'));
            });
    }

    _updateSourceCategories(sourceCategoryIds) {
        this.pendingSourceCategoryIds = sourceCategoryIds;

        this.agent.set({ source_category_ids: sourceCategoryIds }, { silent: true });

        this.sourceCategoryQueue = [...this.sourceCategoryQueue, sourceCategoryIds];

        clearTimeout(this.updateSourceCategoriesTimer);
        this.updateSourceCategoriesTimer = setTimeout(() => {
            this._processSourceCategoryQueue();
        }, 1500);
    }

    _processSourceCategoryQueue() {
        if (this.isProcessingSourceCategories || this.sourceCategoryQueue.length === 0) {return;}

        this.isProcessingSourceCategories = true;
        const finalIds = this.sourceCategoryQueue[this.sourceCategoryQueue.length - 1];
        this.sourceCategoryQueue = [];

        this.agent.set({ source_category_ids: finalIds });

        this._saveSourceCategories()
            .then(() => {
                if (this.pendingSourceGroupIds) {
                    this.agent.set({ source_group_ids: this.pendingSourceGroupIds }, { silent: true });
                }
                this.isProcessingSourceCategories = false;
                if (this.sourceCategoryQueue.length > 0) {
                    this._processSourceCategoryQueue();
                }
            })
            .fail(() => {
                this.isProcessingSourceCategories = false;
            });
    }

    _saveSourceCategories() {
        return this.agent.save(null, { updateOnly: 'source_category_ids' })
            .fail(() => {
                Notificator.showErrorNotification(I18n.t('webapp.agents.edit.agent_subscriptions.request_error'));
            });
    }

    _closeTopicForm() {
        this._deselectSelections();
        this._showEditLandingPageView();
    }

    _deselectSelections(opts) {
        let options = _.extend({}, { except: [] }, opts);

        this._deselectTopicListSelections();
        this._deselectSocialMediaMonitorSelections();
        if (_.contains(options.except, 'SourceSelections')) {
            if (!_.isUndefined(this.editAgentSourceSelectionController)) {
                this.editAgentSourceSelectionController.deselectTimeframeSelection();
            }
        }
        else if (_.contains(options.except, 'TimeframeSelections')) {
            if (!_.isUndefined(this.editAgentSourceSelectionController)) {
                this.editAgentSourceSelectionController.deselectSourceSelections();
            }
        } else {
            if (!_.isUndefined(this.editAgentSourceSelectionController)) {
                this.editAgentSourceSelectionController.deselectAll();
            }
        }
    }

    _deselectTopicListSelections() {
        if (!_.isUndefined(this.editAgentTopicsController)) {
            this.editAgentTopicsController.deselectAll();
        }
    }

    _deselectSocialMediaMonitorSelections() {
        if (!_.isUndefined(this.editAgentSocialMediaMonitorController)) {
            this.editAgentSocialMediaMonitorController.deselectAll();
        }
    }

    _registerListeners() {
        this.listenTo(this.view, 'render', () => {
            this.view.getPreloader().show();
            this._showEditableTitle();
            (new Limits()).fetch({
                success: (limits, _response, _options) => {
                    this.limits = limits;
                    this._showEditAgentSourceSelection();
                    this._showAgentSettings();
                    this._showEditAgentTopics();
                    this._showSocialMediaMonitors();
                    this._updateUnSubscribeButtonVisibility();
                    this.view.getPreloader().hide();
                },
                // TODO meaningful error
                error: () => {
                    this.view.getPreloader().hide();
                    Notificator.showErrorNotification('Limits.fetch() failed')
                }
            });
        });

        this.listenTo(this.agent, 'changed:name', () => {
            this.agent.save(null, {updateOnly: 'name'});
        });

        this.listenTo(this.agent, 'changed:shared', this._updateAgentSharedValue);

        this.listenTo(this.agent, 'change:subscribed', () => {
            this._updateUnSubscribeButtonVisibility();
        });

      this.listenTo(this.agent, 'change:possible_values_for_shared_attribute', () => {
        this._showAgentSettings();
      })
      this.listenTo(this.view, 'showModal',
        this._maybeShowAgentDeletion);
    }

    _updateAgentSharedValue() {
        this.agent.store();
        this.agent.save(null, { updateOnly: 'shared' }).fail((res) => {
            Notificator.showNotification(I18n.t('webapp.agents.edit.agent_subscriptions.request_error'));
            if (res.status === 409) {
                this.agent.fetch();
            } else {
                this.agent.restore();
            }
        });
        this.view.render();
    }

    _setAgentCreationFinishedFlag() {
        this.agent.markAsCreated();
    }

    _showUnsubscribeButton() {
        const controller = new EditAgentUnsubscribeButtonController({
            agent: this.agent
        });
        this.view.showEditAgentUnsubscribeButton(controller.view);
    }

    _updateUnSubscribeButtonVisibility() {
      if (sessionInstance.hasFeature('new_search_agent_management') &&
          this.agent.get('subscribed')) {
        this._showUnsubscribeButton()
      } else {
        if (this.view) {
          this.view.hideUnSubscribeButton()
        }
      }
    }

    _showEditableTitle() {
        this.view.showEditableTitle({
            title: this.agent.get('name'),
            titleFieldName: 'name',
            model: this.agent
        });
    }

    _showEditAgentTopics() {
        this.editAgentTopicsController =
            new EditAgentTopicsController({
                agent: this.agent,
            });

        this.editAgentTopicsView =
            this.editAgentTopicsController.view;

        this.view.showTopics(this.editAgentTopicsView);
        this.listenTo(
            this.editAgentTopicsController,
            'clicked:topic',
            this._showEditTopic
        );

        this.listenTo(
            this.editAgentTopicsController,
            'add:topic',
            this._showNewTopic
        );

        this.listenTo(
            this.editAgentTopicsController,
            'topics:changed',
            this._onTopicSocialMediaMonitorChanged
        );
    }

    _showEditAgentSourceSelection(timeframeProps) {
        this.editAgentSourceSelectionController =
            new EditAgentSourceSelectionController({
                agent: this.agent,
                timeframeProps: timeframeProps
            });

        this.editAgentSourceSelectionView =
            this.editAgentSourceSelectionController.view;

        this.listenTo(
            this.editAgentSourceSelectionView,
            'show:standard:sources',
            this._showSourceGroupAndCategories);

        this.listenTo(
            this.editAgentSourceSelectionController,
            'open:agentTimeframe',
            this._showEditAgentTimeframe
        );

        this.listenTo(
            this.editAgentSourceSelectionView,
            'close:agentTimeframe',
            this._showEditLandingPageView
        );

        this.view.showSourceSelection(this.editAgentSourceSelectionView);
    }

    _showSocialMediaMonitors() {
        this.editAgentSocialMediaMonitorController =
            new EditAgentSocialMediaMonitorController({
                agent: this.agent
            });

        this.editAgentSocialMediaMonitorView =
            this.editAgentSocialMediaMonitorController.view;

        this.view.showSocialMediaMonitors(this.editAgentSocialMediaMonitorView);

        this.listenTo(
            this.editAgentSocialMediaMonitorController,
            'clicked:social_media_monitor',
            this._showSocialMediaMonitorPage
        );

        this.listenTo(
            this.editAgentSocialMediaMonitorController,
            'add:smm',
            this._showCreateSocialMediaMonitor
        );

        this.listenTo(
            this.editAgentSocialMediaMonitorController,
            'social_media_monitors:changed',
            this._onTopicSocialMediaMonitorChanged
        );

        this._onTopicSocialMediaMonitorChanged();
    }

    _onTopicSocialMediaMonitorChanged() {
        const limits = this.limits;
        if (this.agent.topicSocialMediaMonitorLimitReached(limits) || limits.globalTopicSmtLimitReached()) {
            this.editAgentSocialMediaMonitorController.removeAddButton();
            this.editAgentTopicsController.removeAddButton();
        } else {
            this.editAgentSocialMediaMonitorController.addAddButton();
            this.editAgentTopicsController.addAddButton();
        }
    }

    _showAgentSettings() {
        this.editAgentSettingsController =
            new EditAgentSettingsController({
                agent: this.agent,
                readOnly: !AgentsPermissions.canChangeAgentVisibility(this.agent)
            });

        this.editAgentSettingsView =
            this.editAgentSettingsController.view;

        this.view.showEditAgentSettings(this.editAgentSettingsView);
    }

    _showEditAgentTimeframe() {
        this._deselectSelections({ except: ['TimeframeSelections'] });
        this.agentTimeframeController = new AgentTimeframeController({
            agent: this.agent
        });
        this.listenTo(
            this.agentTimeframeController,
            "failed:timeframeSave",
            this._showEditAgentTimeframe
        );
        this.listenTo(
            this.agentTimeframeController,
            "successful:timeframeSave",
            () => {
                this._showEditAgentSourceSelection({ isActive: true });
                this._showEditAgentTimeframe();
            }
        );
        this.showRightHandContainer(this.agentTimeframeController.view());
        vent.trigger('layout:focus-right');
    }

    _maybeShowAgentDeletion() {
        if (!sessionInstance.hasFeature("new_search_agent_management")) {
            if (this.agent.isOwnAgent()) {
                this._showAgentDeletion()
            }
        } else {
            if (this.agent.isOwnAgent() ||
                sessionInstance.isGroupAdmin) {
                this._showAgentDeletion()
            }
        }
    }

    _showAgentDeletion() {
        this.deletionView = new DeletionModalView({
            confirmation: I18n.t('webapp.agents.edit.settings.delete.delete_confirmation',
                                 { name: this.agent.get('name')}),
            warning : I18n.t('webapp.agents.edit.settings.delete.delete_warning'),
            deleteLabel : I18n.t('webapp.agents.edit.settings.delete.delete'),
            cancelLabel : I18n.t('webapp.agents.edit.settings.delete.cancel')
        });
        this.listenTo(this.deletionView, 'modal:delete', this._deleteAgent);
        this.view.showDeletion(this.deletionView);
    }

    _showSocialMediaMonitorPage(smm) {
        this._deselectSelections();
        this.socialMediaMonitorController = new CreateSocialMediaMonitorController({
            socialMediaMonitor: smm
        });

        this.socialMediaMonitorView = this.socialMediaMonitorController.view;

        this.showRightHandContainer(this.socialMediaMonitorView);
        vent.trigger('layout:focus-right');

        this.listenTo(
            this.socialMediaMonitorController,
            'deleted:social_media_monitor',
            this._showEditLandingPageView
        );
    }

    _deleteAgent() {
        this.agent.set('creation_finished', true);
        this.agent.destroy();
        this.trigger('deleted:agent');
    }
}
